import React, { useState, useEffect, useCallback } from 'react';
import useEmblaCarousel, { EmblaOptionsType, UseEmblaCarouselType } from 'embla-carousel-react';

type EmblaViewportRefType = UseEmblaCarouselType[0];
type ButtonScrollEventHandler = (event: React.MouseEvent<HTMLButtonElement>) => void;
type UseCarouselParams = {
  carouselOptions: EmblaOptionsType;
};
export type UseCarouselReturn = {
  carouselRef: EmblaViewportRefType;
  prevBtnEnabled: boolean;
  nextBtnEnabled: boolean;
  selectedIndex: number;
  scrollSnaps: number[] | null;
  scrollPrev: ButtonScrollEventHandler;
  scrollNext: ButtonScrollEventHandler;
  scrollTo: (index: number) => void;
};
export function useCarousel({ carouselOptions }: UseCarouselParams): UseCarouselReturn {
  const [emblaRef, embla] = useEmblaCarousel({ ...carouselOptions });
  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(carouselOptions.startIndex ?? 0);
  const [scrollSnaps, setScrollSnaps] = useState<number[] | null>(null);
  const onSelect = useCallback(() => {
    if (!embla) {
      return;
    }
    setSelectedIndex(embla.selectedScrollSnap());
    setPrevBtnEnabled(embla.canScrollPrev());
    setNextBtnEnabled(embla.canScrollNext());
  }, [embla]);
  useEffect(() => {
    if (!embla) {
      return;
    }
    onSelect();
    const initialScrollSnaps = embla.scrollSnapList();
    setScrollSnaps(initialScrollSnaps.length === 1 ? null : initialScrollSnaps);
    embla.on(`select`, onSelect);
    embla.on(`reInit`, onSelect);
  }, [embla, onSelect]);
  const scrollPrev = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      embla?.scrollPrev();
    },
    [embla]
  );
  const scrollNext = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      embla?.scrollNext();
    },
    [embla]
  );
  const scrollTo = useCallback((index: number) => embla?.scrollTo(index), [embla]);
  return {
    carouselRef: emblaRef,
    prevBtnEnabled,
    nextBtnEnabled,
    selectedIndex,
    scrollSnaps,
    scrollPrev,
    scrollNext,
    scrollTo,
  };
}
